// ---------------------------------
// client/src/pages/AdminJobTypes.jsx
// Admin view for managing job types and their suggested templates
// ---------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import {
    createJobType,
    deleteJobType,
    fetchAllJobTypes,
    fetchFormTemplates,
    updateJobType,
    updateJobTypeActive,
} from '../utils/jobs';

const TEXT = {
    title: 'ניהול סוגי עבודה',
    description: 'הוסף סוגי עבודה וקשר אליהם טפסי PDF מוצעים.',
    add: 'הוסף סוג עבודה',
    edit: 'ערוך',
    deactivate: 'השבת',
    activate: 'הפעל',
    statusActive: 'פעיל',
    statusInactive: 'לא פעיל',
    loading: 'טוען סוגי עבודה...',
    loadError: 'לא ניתן לטעון את סוגי העבודה.',
    empty: 'לא הוגדרו סוגי עבודה.',
    modalTitleCreate: 'סוג עבודה חדש',
    modalTitleEdit: 'עריכת סוג עבודה',
    name: 'שם סוג עבודה',
    descriptionLabel: 'תיאור (אופציונלי)',
    activeLabel: 'פעיל',
    templatesTitle: 'טפסים מוצעים',
    templatesHint: 'בחר טפסים שיופיעו אוטומטית בעבודה מסוג זה.',
    templateRequired: 'חובה',
    displayOrder: 'סדר',
    noOrder: 'ללא',
    cancel: 'ביטול',
    save: 'שמירה',
    saving: 'שומר...',
    saveError: 'לא ניתן לשמור את סוג העבודה.',
};

TEXT.deletePermanent = 'מחיקה מלאה';
TEXT.deleteConfirm = 'למחוק לגמרי את סוג העבודה? הפעולה תיחסם אם קיימות עבודות שמשתמשות בו.';

function buildTemplateSelections(templates, suggestions = []) {
    const suggestionMap = new Map(
        suggestions.map((template) => [String(template.id), template]),
    );
    return templates.map((template) => {
        const suggestion = suggestionMap.get(String(template.id));
        const displayOrderValue =
            suggestion &&
            suggestion.displayOrder !== null &&
            suggestion.displayOrder !== undefined &&
            Number.isFinite(Number(suggestion.displayOrder)) &&
            Number(suggestion.displayOrder) > 0
                ? String(suggestion.displayOrder)
                : '';
        return {
            id: template.id,
            name: template.name,
            isSelected: Boolean(suggestion),
            isRequired: Boolean(suggestion?.isRequired),
            displayOrder: displayOrderValue,
        };
    });
}

export default function AdminJobTypes() {
    const [jobTypes, setJobTypes] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [formError, setFormError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingJobType, setEditingJobType] = useState(null);
    const [formState, setFormState] = useState({
        name: '',
        description: '',
        displayOrder: 0,
        isActive: true,
        templates: [],
    });

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                setError('');
                const [jobTypesData, templatesData] = await Promise.all([
                    fetchAllJobTypes(),
                    fetchFormTemplates(),
                ]);
                setJobTypes(jobTypesData);
                setTemplates(templatesData);
            } catch (err) {
                setError(err.response?.data?.message || TEXT.loadError);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const sortedJobTypes = useMemo(() => {
        return [...jobTypes].sort((a, b) => {
            const orderA = Number.isFinite(Number(a.displayOrder)) ? Number(a.displayOrder) : 0;
            const orderB = Number.isFinite(Number(b.displayOrder)) ? Number(b.displayOrder) : 0;
            if (orderA !== orderB) return orderA - orderB;
            return a.name.localeCompare(b.name, 'he');
        });
    }, [jobTypes]);

    const openCreateModal = () => {
        setEditingJobType(null);
        setFormError('');
        setFormState({
            name: '',
            description: '',
            displayOrder: 0,
            isActive: true,
            templates: buildTemplateSelections(templates),
        });
        setIsModalOpen(true);
    };

    const openEditModal = (jobType) => {
        setEditingJobType(jobType);
        setFormError('');
        setFormState({
            name: jobType.name || '',
            description: jobType.description || '',
            displayOrder: Number.isFinite(Number(jobType.displayOrder)) ? Number(jobType.displayOrder) : 0,
            isActive: jobType.isActive !== false,
            templates: buildTemplateSelections(templates, jobType.suggestedTemplates || []),
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        if (isSaving) return;
        setIsModalOpen(false);
    };

    const toggleTemplateSelection = (templateId) => {
        setFormState((prev) => ({
            ...prev,
            templates: prev.templates.map((template) => {
                if (template.id !== templateId) return template;
                const nextSelected = !template.isSelected;
                return {
                    ...template,
                    isSelected: nextSelected,
                    isRequired: nextSelected ? template.isRequired : false,
                };
            }),
        }));
    };

    const updateTemplateFlag = (templateId, key, value) => {
        setFormState((prev) => ({
            ...prev,
            templates: prev.templates.map((template) => {
                if (template.id !== templateId) return template;
                if (!template.isSelected) {
                    return template;
                }
                return {
                    ...template,
                    [key]: value,
                };
            }),
        }));
    };

    const updateTemplateOrder = (templateId, value) => {
        const parsed = Number(value);
        const nextValue =
            value === "" ? "" : Number.isFinite(parsed) && parsed > 0 ? String(parsed) : "";
        setFormState((prev) => ({
            ...prev,
            templates: prev.templates.map((template) => {
                if (template.id !== templateId) return template;
                if (!template.isSelected) {
                    return template;
                }
                return {
                    ...template,
                    displayOrder: nextValue,
                };
            }),
        }));
    };

    const handleSave = async (event) => {
        event.preventDefault();
        setFormError('');
        if (!formState.name.trim()) {
            setFormError(TEXT.saveError);
            return;
        }
        const suggestedTemplates = formState.templates
            .filter((template) => template.isSelected)
            .map((template) => ({
                templateId: template.id,
                isRequired: template.isRequired,
                displayOrder: (() => {
                    if (template.displayOrder === '' || template.displayOrder === null || template.displayOrder === undefined) {
                        return null;
                    }
                    const parsed = Number(template.displayOrder);
                    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
                })(),
            }));
        try {
            setIsSaving(true);
            const payload = {
                name: formState.name.trim(),
                description: formState.description.trim() || null,
                displayOrder: Number(formState.displayOrder) || 0,
                isActive: formState.isActive,
                suggestedTemplates,
            };
            const saved = editingJobType
                ? await updateJobType(editingJobType.id, payload)
                : await createJobType(payload);
            setJobTypes((prev) => {
                const exists = prev.find((item) => item.id === saved.id);
                if (exists) {
                    return prev.map((item) => (item.id === saved.id ? saved : item));
                }
                return [...prev, saved];
            });
            setIsModalOpen(false);
        } catch (err) {
            setFormError(err.response?.data?.message || TEXT.saveError);
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleActive = async (jobTypeId, isActive) => {
        try {
            const updated = await updateJobTypeActive(jobTypeId, isActive);
            setJobTypes((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        } catch (err) {
            setError(err.response?.data?.message || TEXT.saveError);
        }
    };

    const handleDelete = async (jobTypeId) => {
        if (!window.confirm(TEXT.deleteConfirm)) return;
        try {
            await deleteJobType(jobTypeId);
            setJobTypes((prev) => prev.filter((item) => item.id !== jobTypeId));
        } catch (err) {
            setError(err.response?.data?.message || TEXT.saveError);
        }
    };

    return (
        <div className="page page--admin-job-types page--accent stack stack--lg">
            <header className="page__header">
                <div className="stack">
                    <h1 className="page__title">{TEXT.title}</h1>
                    <p className="page__description">{TEXT.description}</p>
                </div>
                <button type="button" className="button button--primary" onClick={openCreateModal}>
                    {TEXT.add}
                </button>
            </header>

            {error && <div className="alert alert--danger">{error}</div>}

            {isLoading ? (
                <div className="card">{TEXT.loading}</div>
            ) : sortedJobTypes.length === 0 ? (
                <div className="card">{TEXT.empty}</div>
            ) : (
                <div className="card-grid card-grid--two">
                    {sortedJobTypes.map((jobType) => (
                        <article key={jobType.id} className="card">
                            <div className="stack">
                                <div className="flex justify-between align-start">
                                    <h3 className="card__title">{jobType.name}</h3>
                                    <span className={`chip ${jobType.isActive ? 'chip--success' : 'chip--danger'}`}>
                                        {jobType.isActive ? TEXT.statusActive : TEXT.statusInactive}
                                    </span>
                                </div>
                                {jobType.description && <p className="card__meta">{jobType.description}</p>}
                                <p className="card__meta">
                                    {TEXT.templatesTitle}: {(jobType.suggestedTemplates || []).length}
                                </p>
                            </div>
                            <div className="flex flex--wrap">
                                <button
                                    type="button"
                                    className="button button--light"
                                    onClick={() => openEditModal(jobType)}
                                >
                                    {TEXT.edit}
                                </button>
                                <button
                                    type="button"
                                    className={jobType.isActive ? 'button button--danger' : 'button button--primary'}
                                    onClick={() => handleToggleActive(jobType.id, !jobType.isActive)}
                                >
                                    {jobType.isActive ? TEXT.deactivate : TEXT.activate}
                                </button>
                                <button
                                    type="button"
                                    className="button button--danger"
                                    onClick={() => handleDelete(jobType.id)}
                                >
                                    {TEXT.deletePermanent}
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true">
                    <div className="modal-content">
                        <form className="modal-form" onSubmit={handleSave}>
                            <header className="stack">
                                <h2 className="card__title">
                                    {editingJobType ? TEXT.modalTitleEdit : TEXT.modalTitleCreate}
                                </h2>
                            </header>
                            <div className="modal-form__body">
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.name}</span>
                                    <input
                                        className="form-field__control"
                                        type="text"
                                        value={formState.name}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, name: event.target.value }))
                                        }
                                        required
                                    />
                                </label>
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.descriptionLabel}</span>
                                    <textarea
                                        className="form-field__control form-field__control--textarea"
                                        rows={3}
                                        value={formState.description}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, description: event.target.value }))
                                        }
                                    />
                                </label>
                                <label className="check-toggle">
                                    <input
                                        type="checkbox"
                                        checked={formState.isActive}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, isActive: event.target.checked }))
                                        }
                                    />
                                    <span>{TEXT.activeLabel}</span>
                                </label>
                                <div className="stack modal-form__section">
                                    <span className="form-field__label">{TEXT.templatesTitle}</span>
                                    <span className="form-field__description">{TEXT.templatesHint}</span>
                                    <div className="stack">
                                        {formState.templates.map((template) => (
                                            <div key={template.id} className="job-type-form-row">
                                                <label className="check-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={template.isSelected}
                                                        onChange={() => toggleTemplateSelection(template.id)}
                                                    />
                                                    <span className="job-type-form-row__name">{template.name}</span>
                                                </label>
                                                <div className="selector-row__toggles">
                                                    <label className={`check-toggle__option${template.isRequired ? ' check-toggle__option--active' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={template.isRequired}
                                                            onChange={(event) =>
                                                                updateTemplateFlag(template.id, 'isRequired', event.target.checked)
                                                            }
                                                            disabled={!template.isSelected}
                                                        />
                                                        <span>{TEXT.templateRequired}</span>
                                                    </label>
                                                    <label className="form-field form-field--compact">
                                                        <span className="form-field__label">{TEXT.displayOrder}</span>
                                                        <input
                                                            className="form-field__control"
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            value={template.displayOrder}
                                                            placeholder={TEXT.noOrder}
                                                            onChange={(event) =>
                                                                updateTemplateOrder(template.id, event.target.value)
                                                            }
                                                            disabled={!template.isSelected}
                                                        />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            {formError && <div className="alert alert--danger">{formError}</div>}
                            <footer className="modal-form__footer">
                                <button type="button" className="button button--ghost" onClick={closeModal}>
                                    {TEXT.cancel}
                                </button>
                                <button type="submit" className="button button--primary" disabled={isSaving}>
                                    {isSaving ? TEXT.saving : TEXT.save}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
